import type { AzAccountInfo } from "./auth.js";
import type { FoundryConfig } from "./foundry-config.js";
import { type ModelQuota, usageModelName } from "./quota.js";

export type CheckStatus = "PASS" | "WARN" | "FAIL" | "INFO";
export interface CheckResult {
  name: string;
  status: CheckStatus;
  detail: string;
  remediation?: string;
}

export function checkAzSignedIn(az: AzAccountInfo | null): CheckResult {
  if (!az?.tenantId) {
    return { name: "az signed in", status: "FAIL", detail: "not signed in to Azure", remediation: "az login" };
  }
  return { name: "az signed in", status: "PASS", detail: `${az.upn} (tenant ${az.tenantId})` };
}

export function checkFoundryConfig(cfg: FoundryConfig | null): CheckResult {
  if (!cfg) {
    return {
      name: "config.yaml",
      status: "FAIL",
      detail: "missing or invalid ~/.m8t/config.yaml",
      remediation: "m8t switch   (or  m8t config set tenantId|clientId|projectEndpoint <value>)",
    };
  }
  return { name: "config.yaml", status: "PASS", detail: `project ${cfg.projectName}` };
}

export function checkTenantAlignment(az: AzAccountInfo | null, cfg: FoundryConfig | null): CheckResult {
  if (!az || !cfg) {
    return { name: "tenant alignment", status: "WARN", detail: "cannot compare — az or config.yaml missing" };
  }
  if (az.tenantId === cfg.tenantId) {
    return { name: "tenant alignment", status: "PASS", detail: `both ${az.tenantId}` };
  }
  return {
    name: "tenant alignment",
    status: "WARN",
    detail: `az tenant ${az.tenantId} ≠ config tenant ${cfg.tenantId}`,
    remediation: `m8t switch   (or  az login --tenant ${cfg.tenantId})`,
  };
}

export function checkGatewayReachable(probe: { ok: boolean; detail: string }): CheckResult {
  return probe.ok
    ? { name: "gateway reachable", status: "PASS", detail: probe.detail }
    : {
        name: "gateway reachable",
        status: "FAIL",
        detail: probe.detail,
        remediation: "m8t whoami --verbose   to see the discovery/probe error",
      };
}

export function checkDataPlane(probe: {
  status: number;
  foundryAccountId: string | null;
  oid: string | null;
}): CheckResult {
  if (probe.status >= 200 && probe.status < 300) {
    return { name: "foundry data-plane", status: "PASS", detail: `HTTP ${probe.status.toString()}` };
  }
  if (probe.status === 401 || probe.status === 403) {
    const scope = probe.foundryAccountId ?? "<foundry-account-resource-id>";
    const oid = probe.oid ?? "<your-object-id>";
    return {
      name: "foundry data-plane",
      status: "FAIL",
      // Subscription Owner is the natural assumption and it is wrong: reading and
      // invoking agents are dataActions, and control-plane roles carry none.
      detail: `HTTP ${probe.status.toString()} — your identity lacks a data-plane role on the Foundry account (subscription Owner does not grant one)`,
      // "Foundry User", not a Cognitive Services role: Microsoft's guidance is that
      // Cognitive Services roles are for AI Services resources, not Foundry projects.
      remediation:
        `m8t prereqs --fix   (or by hand: az role assignment create --assignee-object-id ${oid} ` +
        `--assignee-principal-type User --role "Foundry User" --scope ${scope})`,
    };
  }
  return {
    name: "foundry data-plane",
    status: "WARN",
    detail: `HTTP ${probe.status !== 0 ? probe.status.toString() : "(unreachable)"} — unexpected; re-run with --verbose`,
  };
}

/**
 * Can YOU read the platform's Key Vault secrets? The install grants its own
 * managed identity `Key Vault Secrets Officer` and grants the human nothing, so
 * every worker deploy the guides tell a founder to run next (`m8t coder deploy`,
 * `m8t azure-exec deploy`) fails on a fresh install. A missing role assignment
 * never heals on its own, so this is a FAIL with a fix — never a retry hint.
 */
export function checkKeyVaultSecrets(probe: {
  status: number;
  kvScope: string | null;
  oid: string | null;
}): CheckResult {
  if (probe.status >= 200 && probe.status < 300) {
    return { name: "key vault secrets", status: "PASS", detail: `HTTP ${probe.status.toString()}` };
  }
  if (probe.status === 401 || probe.status === 403) {
    return {
      name: "key vault secrets",
      status: "FAIL",
      detail: `HTTP ${probe.status.toString()} — you cannot read the platform Key Vault, so worker deploys will fail. This is a permanent permission gap, not a transient blip.`,
      remediation:
        `m8t prereqs --fix   (or by hand: az role assignment create --assignee-object-id ${probe.oid ?? "<your-object-id>"} ` +
        `--assignee-principal-type User --role "Key Vault Secrets User" --scope ${probe.kvScope ?? "<key-vault-resource-id>"})`,
    };
  }
  return {
    name: "key vault secrets",
    status: "INFO",
    detail: probe.status === 0 ? "skipped — could not reach the platform Key Vault" : `HTTP ${probe.status.toString()} — unexpected`,
  };
}

export interface ModelDeployment { name: string; model: string; capacity: number }

export interface DeliveryGrantProbe { agentName: string; hasDeliveryEnv: boolean; hasGrant: boolean }

export function checkDeliveryGrant(probes: DeliveryGrantProbe[]): CheckResult {
  const withDelivery = probes.filter((p) => p.hasDeliveryEnv);
  if (withDelivery.length === 0) {
    return {
      name: "delivery KV grant",
      status: "INFO",
      detail: "no checked agent has delivery creds (GITHUB_APP_INSTALLATION_ID + AZURE_KEYVAULT_URI) set",
    };
  }
  const missing = withDelivery.filter((p) => !p.hasGrant);
  if (missing.length === 0) {
    return {
      name: "delivery KV grant",
      status: "PASS",
      detail: `${withDelivery.map((p) => p.agentName).join(", ")} can read the brain token`,
    };
  }
  return {
    name: "delivery KV grant",
    status: "WARN",
    detail: `${missing.map((p) => p.agentName).join(", ")} ${missing.length === 1 ? "has" : "have"} delivery env but lack Key Vault Secrets User — can't read the brain token (silent empty output)`,
    remediation: `re-run 'm8t coder deploy <name> --env GITHUB_APP_INSTALLATION_ID=… --env AZURE_KEYVAULT_URI=…' (now auto-grants), or grant manually: az role assignment create --role "Key Vault Secrets User" --assignee <agent-principal> --scope <kv-resource-id>`,
  };
}

// Reasoning-family models (the brain/reasoning workers run on these) hit
// 429s at the Foundry default capacity 50 (= 50k TPM) and bumped to 250.
const REASONING_MODEL_RE = /^(gpt-5|o1|o3|o4)/i;
const MIN_REASONING_CAPACITY = 250;

export function checkReasoningCapacity(
  deployments: ModelDeployment[],
  threshold = MIN_REASONING_CAPACITY,
): CheckResult {
  const reasoning = deployments.filter((d) => REASONING_MODEL_RE.test(d.model));
  if (reasoning.length === 0) {
    return {
      name: "reasoning model capacity",
      status: "INFO",
      detail: "no reasoning-family (gpt-5*/o*) model deployment found — brain/reasoning workers need one provisioned",
    };
  }
  const under = reasoning.filter((d) => d.capacity < threshold);
  if (under.length === 0) {
    return {
      name: "reasoning model capacity",
      status: "PASS",
      detail: reasoning.map((d) => `${d.name} (${d.model}) capacity ${d.capacity.toString()}`).join(", "),
    };
  }
  return {
    name: "reasoning model capacity",
    status: "WARN",
    detail: `${under
      .map((d) => `${d.name} (${d.model}) capacity ${d.capacity.toString()} < ${threshold.toString()}`)
      .join("; ")} — heavy/brain turns may 429 (each unit ≈ 1k TPM)`,
    remediation: `raise to ≥${threshold.toString()}: az cognitiveservices account deployment create -g <rg> -n <account> --deployment-name ${under[0].name} --sku-capacity ${threshold.toString()} --model-name <model> --model-format OpenAI --model-version <ver> (quota permitting; see deploy/README.md Prerequisites)`,
  };
}

export function checkModelQuota(deployments: ModelDeployment[], usages: ModelQuota[]): CheckResult {
  if (usages.length === 0) {
    return { name: "model quota", status: "INFO", detail: "could not read 'az cognitiveservices usage list'" };
  }
  const zero = deployments.filter((d) => {
    if (!d.model) return false;
    const dt = d.model.toLowerCase();
    const u = usages.filter((x) => usageModelName(x.name).toLowerCase() === dt);
    return u.length > 0 && u.every((x) => x.limit === 0);
  });
  if (zero.length === 0) {
    const quotad = [...new Set(usages.filter((u) => u.limit > 0 && /gpt-5|o[1-9]/i.test(u.name)).map((u) => usageModelName(u.name)))];
    return { name: "model quota", status: "PASS", detail: `quota'd reasoning models: ${quotad.join(", ") || "(none gpt-5*/o*)"}` };
  }
  return {
    name: "model quota",
    status: "WARN",
    detail: `deployed model(s) with 0 quota: ${zero.map((d) => `${d.name} (${d.model})`).join(", ")} — redeploy/scale will fail`,
    remediation: "switch to a quota'd model or request quota (see 'az cognitiveservices usage list -l <region>')",
  };
}

export interface RepoRootProbe {
  markerExists: boolean;        // ~/.m8t/repo-root file present
  path: string | null;         // trimmed marker contents (the checkout path), or null when absent
  isDir: boolean;              // the path resolves to an existing directory
  isGitCheckout: boolean;      // <path>/.git exists (real, current checkout)
}

export function checkRepoRoot(probe: RepoRootProbe): CheckResult {
  if (!probe.markerExists || !probe.path) {
    return {
      name: "brain repo-root",
      status: "FAIL",
      detail: "~/.m8t/repo-root marker missing — m8t install never completed",
      remediation: "re-run the m8t install (writes ~/.m8t/repo-root), or: printf %s \"$(git -C <m8t-checkout> rev-parse --show-toplevel)\" > ~/.m8t/repo-root",
    };
  }
  if (!probe.isDir) {
    return {
      name: "brain repo-root",
      status: "FAIL",
      detail: `~/.m8t/repo-root → ${probe.path} — that path does not exist or is not a directory`,
      remediation: "the m8t checkout moved or was deleted; point the marker at the current checkout: printf %s \"$(git -C <m8t-checkout> rev-parse --show-toplevel)\" > ~/.m8t/repo-root",
    };
  }
  if (!probe.isGitCheckout) {
    return {
      name: "brain repo-root",
      status: "WARN",
      detail: `~/.m8t/repo-root → ${probe.path} exists but is not a git checkout (no .git) — brain-loader template + persona paths may be stale`,
      remediation: "point the marker at the m8t git checkout (it reads targets/foundry/brain-loader.md from there)",
    };
  }
  return { name: "brain repo-root", status: "PASS", detail: `~/.m8t/repo-root → ${probe.path}` };
}

export interface LegacyStateDirProbe {
  legacyExists: boolean;  // ~/.m8t-stack (pre-rename state dir) present
  currentExists: boolean; // ~/.m8t present
}

export function checkLegacyStateDir(probe: LegacyStateDirProbe): CheckResult {
  if (!probe.legacyExists) {
    return { name: "state dir", status: "PASS", detail: "no legacy ~/.m8t-stack folder" };
  }
  if (!probe.currentExists) {
    return {
      name: "state dir",
      status: "WARN",
      detail: "found legacy ~/.m8t-stack but no ~/.m8t — the state folder was renamed to ~/.m8t",
      remediation: "mv ~/.m8t-stack ~/.m8t",
    };
  }
  return {
    name: "state dir",
    status: "WARN",
    detail: "legacy ~/.m8t-stack exists alongside ~/.m8t — only ~/.m8t is read",
    remediation: "review ~/.m8t-stack for anything you still need, then remove it",
  };
}
